import { useSmartActionsGate } from "@/features/shared/feature-gates";
import useUserRole from "@/hooks/use-user-role";

export type SmartActionsAccess = {
  /** The person may use Smart Actions. False while either check is loading. */
  allowed: boolean;
  /** Statsig or the signed-in user is still loading: guards wait, hiders may ignore it. */
  isLoading: boolean;
  /** Which check failed once both have loaded; null while loading or when allowed. */
  deniedBy: "role" | "gate" | null;
};

/**
 * Who may use Smart Actions: the `rollout_smart_actions` gate AND the ADMIN or
 * MANAGER role ("Service Coordinator"). The platform refuses every other role,
 * so a surface shown to them would only render errors.
 *
 * A route guard must wait while `isLoading`: a cold Statsig cache reads as
 * "gate off", and bouncing on it would eject someone who is allowed in. A
 * surface that only hides (a sidebar row, the order card) reads `allowed`.
 */
export function useSmartActionsAccess(): SmartActionsAccess {
  const gate = useSmartActionsGate();
  const { isAdmin, isManager, isLoading: isRoleLoading } = useUserRole();

  const isLoading = gate.isLoading || isRoleLoading;
  if (isLoading) return { allowed: false, isLoading, deniedBy: null };

  const deniedBy = !(isAdmin || isManager)
    ? "role"
    : !gate.enabled
      ? "gate"
      : null;
  return { allowed: deniedBy === null, isLoading, deniedBy };
}
